import React from 'react';
import Chart, { ChartConf } from 'src/components/charts/Chart/Chart';
import { getMinMax, parseRiskTolerance, plotLineGradient } from 'src/components/charts/util';
import { AssessmentQuery } from 'src/graphql/_generated';
import { useStateSelector } from 'src/redux';

type Assessment = NonNullable<AssessmentQuery['assessment']>;

interface VersionedChartProps {
  isLogarithmic: boolean;
  isLegacyVersion: boolean;
  showAllProjected?: boolean;
  live: Assessment['AssessmentLiveRisk'];
  base?: Assessment['AssessmentBaseRisk'];
  projected?: Assessment['AssessmentProjectedRisk'];
  scenarios: Assessment['scenarios'];
  riskTolerance: Assessment['riskTolerance'];
}

export const VersionedChart: React.FC<VersionedChartProps> = ({
  isLogarithmic,
  isLegacyVersion,
  showAllProjected,
  base,
  live,
  projected,
  scenarios,
  riskTolerance = '',
}) => {
  const colors = useStateSelector(({ theme }) => theme.colors);

  const validBaseRisks = scenarios.map((el) => el.baseRisk).filter((risk) => risk.upper != null && risk.lower != null);
  const validLiveRisks = scenarios.map((el) => el.liveRisk).filter((risk) => risk.upper != null && risk.lower != null);
  const validCompleteRisks = scenarios
    .map((el) => el.projectedRisk)
    .filter((risk) => risk.upper != null && risk.lower != null);

  const input = !isLegacyVersion
    ? ([
        showAllProjected
          ? {
              data: base?.chart || [],
              upper: getMinMax(validBaseRisks).upper || 1,
              lower: getMinMax(validBaseRisks).lower || 1,
              name: 'Unmodified Risk',
              color: colors.prussianBlue,
              fillColor: 'rgba(2, 3, 51, 0.07)',
              legendId: 'assessment_leg-base-m',
            }
          : null,
        showAllProjected
          ? {
              data: projected?.chart || [],
              upper: getMinMax(validCompleteRisks).upper || 1,
              lower: getMinMax(validCompleteRisks).lower || 1,
              name: 'Projected Risk',
              color: colors.brightBlue,
              fillColor: 'rgba(100, 149, 237, 0.07)',
              legendId: 'assessment_leg-s2-m',
            }
          : null,
        {
          data: live?.chart || [],
          upper: getMinMax(validLiveRisks).upper || 1,
          lower: getMinMax(validLiveRisks).lower || 1,
          name: 'Live Risk',
          color: plotLineGradient,
          fillColor: 'rgba(230, 46, 97, 0.07)',
          legendId: 'assessment_leg-s1-m',
        },
        {
          data: parseRiskTolerance(riskTolerance),
          name: 'Risk Tolerance',
          type: 'spline',
          dashStyle: 'ShortDash',
          legendId: 'assessment_leg-rt',
          fillColor: colors.cflowerBlue,
          dataLabels: {
            enabled: false,
          },
          color: colors.cflowerBlue,
          isRiskTolerance: true,
        },
      ].filter(Boolean) as ChartConf[])
    : [
        {
          data: base?.chart || [],
          upper: getMinMax(validBaseRisks).upper || 1,
          lower: getMinMax(validBaseRisks).lower || 1,
          name: 'Loss Exceedance',
          color: colors.gp2,
          fillColor: colors.aliceBlue,
          legendId: 'scenario_legend1',
        },
        {
          data: parseRiskTolerance(riskTolerance),
          name: 'Risk Tolerance',
          type: 'spline',
          dashStyle: 'ShortDash',
          legendId: 'assessment_leg-rt',
          fillColor: colors.cflowerBlue,
          dataLabels: {
            enabled: false,
          },
          color: colors.cflowerBlue,
          isRiskTolerance: true,
        },
      ];

  return <Chart isLogarithmic={isLogarithmic} input={input} />;
};
